'use client'

import type { ChangeEvent, FormEvent } from 'react'
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { IconGoogle } from '@/components/icons'
import { Logo } from '@/components/logo'
import Image from 'next/image'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import sendToMixpanel from '@/lib/mixpanel/sendToMixpanel'
import { useTranslations } from 'next-intl'
import { getReferredByCode } from '@/lib/referral_code'
import { nanoid } from 'nanoid'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import Turnstile, { useTurnstile } from 'react-turnstile'

interface FormData {
  email: string
  verificationCode: string
}

export function LoginSection({ redirectUrl }: { redirectUrl: string }) {
  const t = useTranslations('login')
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [showVerification, setShowVerification] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [hasResent, setHasResent] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [resendTurnstileToken, setResendTurnstileToken] = useState<
    string | null
  >(null)
  const [isMounted, setIsMounted] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    email: '',
    verificationCode: '',
  })

  // Set mounted state to prevent hydration mismatch
  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  useEffect(() => {
    if (formData.verificationCode.length === 6) {
      handleVerificationSubmit()
    }
  }, [formData.verificationCode])

  const handleResendCode = async () => {
    if (countdown > 0) return
    // show turnstile widget and if it's verified, then resend the code
    setHasResent(true)
  }

  const handleResendTurnstileToken = async (token: string) => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/auth/email-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify({
          email: formData.email,
          turnstileToken: token,
        }),
      })

      if (!response.ok) {
        // if status is 429, show error message
        if (response.status === 429) {
          throw new Error('Too many requests, please try again later')
        }
        throw new Error('Failed to resend verification code')
      }

      toast.success('Verification code resent')

      setCountdown(60)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to resend code',
      )
    } finally {
      setIsLoading(false)
      setHasResent(false)
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [id.replace('signin-', '').replace('signup-', '')]: value,
    }))
  }

  const handleEmailSignIn = async (e: FormEvent) => {
    e.preventDefault()
    if (!turnstileToken) {
      toast.error('Please complete the security check')
      return
    }

    setIsLoading(true)

    sendToMixpanel(MIXPANEL_CLIENT_EVENT_NAMES.CLICK_SIGNIN, {
      email: formData.email,
      source: 'email',
    })

    const referralCode = getReferredByCode()

    try {
      const response = await fetch('/api/auth/email-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify({
          email: formData.email,
          referralCode,
          turnstileToken,
        }),
      })

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Too many requests, please try again later')
        }
        throw new Error('Failed to send verification code')
      }

      setShowVerification(true)
      setHasResent(false)
      setCountdown(0)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to send verification code',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerificationSubmit = async () => {
    setIsLoading(true)
    sendToMixpanel(MIXPANEL_CLIENT_EVENT_NAMES.CLICK_VERIFY_CODE, {
      email: formData.email,
    })
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify({
          email: formData.email,
          verificationCode: formData.verificationCode,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed')
      }

      toast.success('Signed in successfully')
      setShowVerification(false)
      window.location.href = redirectUrl || '/'
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Verification failed',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const renderVerificationContent = () => (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="p-0 hover:bg-transparent text-gray-900"
          onClick={() => setShowVerification(false)}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Back to login form</title>
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>
        <h2 className="flex-1 text-center text-lg font-bold text-gray-900">
          {t('check_email_title')}
        </h2>
      </div>

      <div className="text-center text-gray-500 mb-8">
        {t('sent_code_to')}
        <br />
        <span className="font-medium text-gray-700">{formData.email}</span>
        <br />
        {t('confirm_with_code')}
      </div>

      <div className="flex justify-center gap-2">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Input
            key={i}
            type="text"
            maxLength={1}
            className="w-12 h-12 text-center text-xl rounded-lg text-dark bg-white"
            value={formData.verificationCode[i] || ''}
            onChange={(e) => {
              const value = e.target.value
              if (value.length > 1) {
                const pastedValue = value.slice(0, 6)
                setFormData((prev) => ({
                  ...prev,
                  verificationCode: pastedValue,
                }))
                if (pastedValue.length === 6) {
                  const inputs = document.getElementsByName(`verification-${5}`)
                  if (inputs[0]) {
                    inputs[0].focus()
                  }
                }
              } else {
                setFormData((prev) => {
                  const newVerificationCode =
                    prev.verificationCode.substring(0, i) +
                    value +
                    prev.verificationCode.substring(i + 1)
                  return {
                    ...prev,
                    verificationCode: newVerificationCode,
                  }
                })
                if (value) {
                  const nextInput = document.getElementsByName(
                    `verification-${i + 1}`,
                  )
                  if (nextInput[0]) {
                    nextInput[0].focus()
                  }
                }
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !formData.verificationCode[i]) {
                const prevInput = document.getElementsByName(
                  `verification-${i - 1}`,
                )
                if (prevInput[0]) {
                  prevInput[0].focus()
                }
              }
            }}
            onPaste={(e) => {
              e.preventDefault()
              const pastedData = e.clipboardData.getData('text').slice(0, 6)
              setFormData((prev) => ({
                ...prev,
                verificationCode: pastedData,
              }))
            }}
            name={`verification-${i}`}
          />
        ))}
      </div>

      <Button
        className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-base font-medium"
        onClick={handleVerificationSubmit}
        disabled={
          isLoading ||
          !formData.verificationCode ||
          formData.verificationCode.length !== 6
        }
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('verifying')}
          </>
        ) : (
          t('verify_code')
        )}
      </Button>

      <div className="flex flex-col items-center space-y-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleResendCode}
          disabled={countdown > 0 || isLoading}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          {countdown > 0
            ? t('resend_code_in', { seconds: countdown })
            : t('resend_code')}
        </Button>
        {hasResent && (
          <Turnstile
            sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''}
            onVerify={(token) => handleResendTurnstileToken(token)}
            theme="light"
            refreshExpired="auto"
            fixedSize={true}
          />
        )}
      </div>
    </div>
  )

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 relative overflow-hidden">
        {/* Logo */}
        <div className="absolute top-6 left-6 md:top-8 md:left-8 z-10">
          <Logo variant="cyber" />
        </div>

        {/* Background Decorations */}
        <div className="absolute top-10 right-10 hidden md:block">
          <div className="w-16 h-16 border border-white/10 relative">
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-pink-500" />
          </div>
        </div>
        <div className="absolute bottom-20 left-20 hidden md:block">
          <div className="w-24 h-24 border border-white/10 relative">
            <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-yellow-400" />
          </div>
        </div>
        <div className="absolute right-40 bottom-40 hidden md:block">
          <div className="w-20 h-20 border border-white/10 relative">
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400" />
          </div>
        </div>

        {/* Content Container */}
        <div className="lg:grid lg:grid-cols-2 w-full mx-auto min-h-screen">
          {/* Left side with text */}
          <div className="flex items-center justify-center font-brand lg:justify-start lg:pl-20 pt-20 lg:pt-0 px-6 md:px-0">
            <div className="max-w-2xl text-white">
              <div className="relative">
                <h1 className="text-[40px] md:text-[60px] font-black mb-4 leading-tight flex items-center">
                  <span className="text-[#00D487] mr-2 text-[32px] md:text-[40px] font-light">
                    {'{'}
                  </span>
                  <span className="text-[#FFE500]">{t('hero.hey_boss')}</span>
                  <span className="text-white text-[36px] md:text-[50px] ml-2">
                    ,
                  </span>
                </h1>
                <h2 className="text-[32px] md:text-[42px] font-black leading-tight text-white flex flex-wrap md:flex-nowrap items-center">
                  <span className="whitespace-nowrap">
                    {t('hero.what_will')}
                  </span>
                  <span className="relative mx-2 px-6 md:px-8 py-2 my-2 md:my-0">
                    <Image
                      src="/cursor.svg"
                      alt=""
                      width={40}
                      height={40}
                      className="absolute -top-16 left-1/2 -translate-x-1/2 rotate-[-130deg] w-8 md:w-10"
                    />
                    <Image
                      src="/border.svg"
                      alt=""
                      width={200}
                      height={100}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[160%]"
                    />
                    <span className="text-[#FFE500] relative">
                      {t('hero.build')}
                    </span>
                  </span>{' '}
                  {t('hero.today')}
                  <span className="text-[#FF90AD] ml-2 text-[32px] md:text-[40px] font-light">
                    {'}'}
                  </span>
                </h2>
              </div>
              <p className="text-[20px] md:text-[28px] mt-6 text-white/60">
                {t('hero.subtitle')}
              </p>
            </div>
          </div>

          {/* Right side with login form */}
          <div className="flex items-center justify-center p-6 lg:p-0 mt-10 md:mt-0">
            <div className="bg-white rounded-3xl p-6 md:p-8 w-full max-w-[400px] lg:max-w-[480px] shadow-xl">
              {!showVerification ? (
                <>
                  <h2 className="text-lg font-bold mb-6 md:mb-8 text-gray-900">
                    {t('welcome')}
                  </h2>
                  <Tabs defaultValue="signin" className="w-full">
                    <GoogleLoginButton redirectUrl={redirectUrl} />

                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">
                          {t('or_continue_with_mail')}
                        </span>
                      </div>
                    </div>

                    <TabsContent value="signin">
                      <form onSubmit={handleEmailSignIn} className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-dark" htmlFor="signin-email">
                            {t('email')}
                          </Label>
                          <Input
                            id="signin-email"
                            type="email"
                            placeholder={t('email_placeholder')}
                            required
                            value={formData.email}
                            onChange={handleInputChange}
                            className="text-dark bg-white"
                          />
                        </div>

                        {/* Cloudflare Turnstile Widget using react-turnstile package */}
                        {isMounted && (
                          <div className="flex justify-center my-4">
                            <Turnstile
                              sitekey={
                                process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''
                              }
                              onVerify={(token) => setTurnstileToken(token)}
                              theme="light"
                              refreshExpired="auto"
                              fixedSize={true}
                            />
                          </div>
                        )}

                        <Button
                          type="submit"
                          className="w-full"
                          disabled={isLoading || (!turnstileToken && isMounted)}
                        >
                          {isLoading
                            ? t('signing_in')
                            : t('sign_in_with_email')}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>
                </>
              ) : (
                renderVerificationContent()
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export function GoogleLoginButton({ redirectUrl }: { redirectUrl: string }) {
  const t = useTranslations('login')
  const handleLogin = () => {
    const referralCode = getReferredByCode()
    sendToMixpanel(MIXPANEL_CLIENT_EVENT_NAMES.CLICK_SIGNIN, {
      source: 'google',
    })
    window.location.href = `/api/login/google?redirectUrl=${encodeURIComponent(
      redirectUrl,
    )}&referralCode=${encodeURIComponent(referralCode || '')}`
  }

  return (
    <Button
      variant="outline"
      size="lg"
      className="w-full"
      onClick={handleLogin}
    >
      <IconGoogle />
      {t('sign_in_with_google')}
    </Button>
  )
}
